import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CompanyContext = createContext(null);
const STORAGE_KEY = "fstudy_active_company_id";

export function CompanyProvider({ children }) {
  const { user, status } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (status !== "authenticated") return;
    setLoading(true);
    try {
      const { data } = await api.get("/companies/mine");
      setCompanies(data);
      // Elegir empresa activa: la guardada si sigue siendo válida, sino la primera.
      if (!data.length) {
        setActiveIdState(null);
      } else if (!activeId || !data.find((c) => c.id === activeId)) {
        const first = data[0].id;
        setActiveIdState(first);
        localStorage.setItem(STORAGE_KEY, first);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [status, activeId]);

  useEffect(() => { load(); }, [load, user?.id]);

  const setActiveId = (id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const active = companies.find((c) => c.id === activeId) || null;

  const effectiveHasModule = (mod) => {
    if (!active) return false;
    return Boolean(active.effective_modules?.[mod]);
  };

  return (
    <CompanyContext.Provider value={{
      companies, active, activeId, setActiveId,
      loading, reload: load, effectiveHasModule,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany requires CompanyProvider");
  return ctx;
}

export function getActiveCompanyId() {
  return localStorage.getItem(STORAGE_KEY);
}
